import { ChangeDetectorRef, Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { SideDrawerComponent } from '@app/core/components/side-drawer-container/side-drawer/side-drawer.component';
import { AiChatService } from '@app/core/services/ai-chat.service';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import {
    AiChatItem,
    AiChatPromptContent,
    AiModelIntelligenceItem,
    AiModelItem,
    QueueAiPromptResponse,
    SendAiPromptParams,
} from '@app/core/services/ai-chat.types';
import { RememberService } from '@app/core/services/remember.service';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { AiChatPanelService } from './ai-chat-panel.service';

@Component({
    selector: 'tt-ai-chat',
    templateUrl: './ai-chat.component.html',
    styleUrls: ['./ai-chat.component.css'],
    standalone: false,
})
export class AiChatComponent implements OnInit {
    @ViewChild('conversationDrawer')
    private chatsDrawer?: SideDrawerComponent;

    public readonly chats$ = this.aiChat.chats$.asObservable();

    private readonly selectedChatKeyno$ = new BehaviorSubject<string | null>(null);

    public readonly messages$ = combineLatest([this.selectedChatKeyno$, this.aiChat.messagesByChat$]).pipe(
        map(([chatKeyno, messagesByChat]) => (chatKeyno ? messagesByChat[chatKeyno] ?? [] : [])),
    );

    public readonly models$ = this.aiChat.models$.asObservable();

    public selectedChat?: AiChatItem;

    public selectedModelKeyno = '';

    public intelligenceLevels: AiModelIntelligenceItem[] = [];

    public selectedIntelligenceLevel = '';

    public loadingIntelligenceLevels = false;

    public prompt = '';

    public loading = true;

    public loadingMessages = false;

    public sending = false;

    public errorMessage = '';

    /** The pop-out action is hidden while this component is already in a dialog. */
    public readonly canOpenInModal: boolean;

    private chatContextVersion = 0;

    private intelligenceLevelRequestVersion = 0;

    private readonly selectedChatRememberId = 'w_ai_chat.selected_chat_keyno';

    private newConversationModelKeyno = '';

    private newConversationIntelligenceLevel = '';

    private pendingPrompt?: Promise<QueueAiPromptResponse>;

    private aborting = false;

    private stoppingMessageKeyno?: string;

    constructor(
        private aiChat: AiChatService,
        private cdr: ChangeDetectorRef,
        private remember: RememberService,
        public panel: AiChatPanelService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.canOpenInModal = navigator?.host !== 'dialog';
    }

    public async selectConversation(conversation: AiChatItem, rememberSelection = true): Promise<void> {
        this.closeConversationDrawer();

        if (this.loadingMessages || conversation.ai_chat_keyno === this.selectedChat?.ai_chat_keyno) {
            return;
        }

        this.selectedChat = conversation;
        this.selectedChatKeyno$.next(conversation.ai_chat_keyno);
        this.chatContextVersion++;
        this.loadingMessages = true;
        this.errorMessage = '';

        if (rememberSelection) {
            void this.remember.remember(this.selectedChatRememberId, conversation.ai_chat_keyno).catch(() => undefined);
        }

        try {
            const messages = await this.aiChat.getChatMessages({ ai_chat_keyno: conversation.ai_chat_keyno, num_of_rows: 21 });
            const lastMessage = messages[messages.length - 1];
            const lastMessageModelKeyno = lastMessage?.ai_model_keyno;
            const providerModels = this.aiChat.models$.value.filter((model) => model.ai_provider_keyno === conversation.ai_provider_keyno);
            const selectedModel = providerModels.find((model) => model.ai_model_keyno === lastMessageModelKeyno) ?? providerModels[0];

            const selectedModelKeyno = selectedModel?.ai_model_keyno ?? this.selectedModelKeyno;
            this.newConversationModelKeyno = selectedModelKeyno;
            await this.selectModel(selectedModelKeyno, false);
            this.setNewConversationIntelligenceLevel(lastMessage?.intelligence_level ?? '');
        } catch {
            this.errorMessage = 'The conversation could not be loaded.';
        } finally {
            this.loadingMessages = false;
        }
    }

    public startNewConversation(): void {
        this.closeConversationDrawer();
        this.chatContextVersion++;
        this.newConversationModelKeyno = this.selectedModelKeyno || this.newConversationModelKeyno;
        this.newConversationIntelligenceLevel = this.selectedIntelligenceLevel;
        this.selectedChat = undefined;
        this.selectedChatKeyno$.next(null);
        this.prompt = '';
        this.errorMessage = '';
        void this.remember.remember(this.selectedChatRememberId, 0).catch(() => undefined);

        if (this.newConversationModelKeyno) {
            void this.restoreNewConversationSettings();
        }
    }

    public async selectModel(modelKeyno: string, rememberModel = true): Promise<void> {
        this.selectedModelKeyno = modelKeyno;

        if (rememberModel && this.aiChat.models$.value.some((model) => model.ai_model_keyno === modelKeyno)) {
            this.newConversationModelKeyno = modelKeyno;
        }

        const requestVersion = ++this.intelligenceLevelRequestVersion;

        if (!modelKeyno) {
            this.intelligenceLevels = [];
            this.selectedIntelligenceLevel = '';
            this.loadingIntelligenceLevels = false;
            return;
        }

        this.loadingIntelligenceLevels = true;

        try {
            const levels = await this.aiChat.getAiModelIntelligence({ ai_model_keyno: modelKeyno });
            if (requestVersion !== this.intelligenceLevelRequestVersion || modelKeyno !== this.selectedModelKeyno) {
                return;
            }

            this.intelligenceLevels = [...levels].sort((left, right) => Number(left.orderby) - Number(right.orderby));
            this.selectedIntelligenceLevel = this.intelligenceLevels[0]?.intelligence_level_id ?? '';
        } catch {
            if (requestVersion !== this.intelligenceLevelRequestVersion || modelKeyno !== this.selectedModelKeyno) {
                return;
            }

            this.intelligenceLevels = [];
            this.selectedIntelligenceLevel = '';
        } finally {
            if (requestVersion === this.intelligenceLevelRequestVersion && modelKeyno === this.selectedModelKeyno) {
                this.loadingIntelligenceLevels = false;
            }
        }
    }

    /** Queues the composer HTML together with its plain-text provider content. */
    public async sendPrompt(composerPrompt: AiChatPromptContent = { textcontent: '', imageUrls: [] }): Promise<void> {
        const prompt = this.prompt.trim();
        const promptTextcontent = composerPrompt.textcontent.trim();

        if ((!promptTextcontent && composerPrompt.imageUrls.length === 0) || !this.selectedModelKeyno || this.loadingIntelligenceLevels || this.sending || this.activePrompt || this.stopping) {
            return;
        }

        const chatKeyno = this.selectedChat?.ai_chat_keyno ?? 0;
        const contextVersion = this.chatContextVersion;
        this.sending = true;
        this.errorMessage = '';

        try {
            this.pendingPrompt = this.aiChat.sendPrompt(this.createSendPromptParams(prompt, promptTextcontent, composerPrompt));
            const queued = await this.pendingPrompt;

            this.prompt = '';

            if (`${chatKeyno}` === '0' && contextVersion === this.chatContextVersion) {
                this.selectedChatKeyno$.next(queued.ai_chat_keyno);
                void this.refreshQueuedChat(queued.ai_chat_keyno, contextVersion);
            }
        } catch (error) {
            this.errorMessage = error instanceof Error ? error.message : 'The message could not be sent. Please try again.';
        } finally {
            this.pendingPrompt = undefined;
            this.sending = false;
        }
    }

    public get activePrompt() {
        const chatKeyno = this.selectedChatKeyno$.value;
        return chatKeyno ? this.aiChat.messagesByChat$.value[chatKeyno]?.find((message) => message.polling) : undefined;
    }

    public get stopping() {
        const activePrompt = this.activePrompt;
        return this.aborting || (!!activePrompt && activePrompt.ai_message_keyno === this.stoppingMessageKeyno);
    }

    public async abortPrompt(): Promise<void> {
        const activePrompt = this.activePrompt;
        const pendingPrompt = this.pendingPrompt;
        if (this.stopping || (!activePrompt && !pendingPrompt)) return;

        this.aborting = true;
        this.errorMessage = '';

        try {
            // A stop clicked during queueing waits for the message identifiers.
            const prompt = activePrompt ?? (await pendingPrompt);
            if (!prompt) return;

            this.stoppingMessageKeyno = prompt.ai_message_keyno;
            const response = await this.aiChat.abortPrompt({ ai_chat_keyno: prompt.ai_chat_keyno, ai_message_keyno: prompt.ai_message_keyno });
            if (`${response.errorcode}` !== '0') throw new Error(response.errormessage || 'The prompt could not be stopped.');
        } catch (error) {
            this.stoppingMessageKeyno = undefined;
            this.errorMessage = error instanceof Error ? error.message : 'The prompt could not be stopped. Please try again.';
        } finally {
            this.aborting = false;
        }
    }

    private async loadInitialData() {
        this.loading = true;
        this.errorMessage = '';

        try {
            const [chats, models] = await Promise.all([this.aiChat.getChats(), this.aiChat.getAiModels()]);
            const settings = this.panel.settings;
            const selectedModelKeyno = this.getNewConversationModelKeyno(models);
            const requestedChatKeyno = this.getRequestedChatKeyno();

            this.newConversationModelKeyno = selectedModelKeyno;
            this.newConversationIntelligenceLevel = '';
            const selectedChatKeyno = requestedChatKeyno ?? settings?.selected_chat_keyno;
            const selectedChat = selectedChatKeyno && selectedChatKeyno !== '0' ? chats.find((chat) => chat.ai_chat_keyno === selectedChatKeyno) : undefined;

            if (selectedChat) {
                await this.selectConversation(selectedChat, false);
            } else {
                await this.selectModel(selectedModelKeyno, false);
            }
        } catch {
            this.errorMessage = 'AI chat could not be loaded.';
        } finally {
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    private async refreshQueuedChat(aiChatKeyno: string, contextVersion: number) {
        try {
            const chats = await this.aiChat.getChats();

            if (contextVersion === this.chatContextVersion) {
                this.selectedChat = chats.find((chat) => chat.ai_chat_keyno === aiChatKeyno);
                if (this.selectedChat) void this.remember.remember(this.selectedChatRememberId, aiChatKeyno).catch(() => undefined);
            }
        } catch {
            // The queued message and its poll continue even if refreshing the chat list fails.
        }
    }

    private createSendPromptParams(prompt: string, promptTextcontent: string, composerPrompt: AiChatPromptContent): SendAiPromptParams {
        return {
            ai_chat_keyno: this.selectedChat?.ai_chat_keyno ?? 0,
            ai_model_keyno: this.selectedModelKeyno as `${number}`,
            intelligence_level: this.selectedIntelligenceLevel,
            prompt,
            prompt_textcontent: promptTextcontent,
            prompt_image_urls: composerPrompt.imageUrls,
        };
    }

    private getNewConversationModelKeyno(models: AiModelItem[]) {
        const settings = this.panel.settings;
        const model = models.find((item) => item.ai_model_keyno === settings?.ai_model_keyno && item.ai_provider_keyno === settings.ai_provider_keyno);
        return model?.ai_model_keyno ?? models[0]?.ai_model_keyno ?? '';
    }

    /** Returns the chat explicitly requested by the dialog route, if one was supplied. */
    private getRequestedChatKeyno(): string | undefined {
        const chatKeyno = this.navigator?.params['ai_chat_keyno'];
        return typeof chatKeyno === 'string' || typeof chatKeyno === 'number' ? `${chatKeyno}` : undefined;
    }

    private async restoreNewConversationSettings() {
        await this.selectModel(this.newConversationModelKeyno, false);
        this.setNewConversationIntelligenceLevel(this.newConversationIntelligenceLevel);
    }

    private setNewConversationIntelligenceLevel(intelligenceLevel: string) {
        if (this.intelligenceLevels.some((item) => item.intelligence_level_id === intelligenceLevel)) {
            this.selectedIntelligenceLevel = intelligenceLevel;
        }

        this.newConversationIntelligenceLevel = this.selectedIntelligenceLevel;
    }

    public showError(message: string) {
        this.errorMessage = message;
    }

    public toggleConversationDrawer() {
        void this.chatsDrawer?.toggle();
    }

    /** Opens the routed chat view so StateModal supplies HelpDoc and video actions. */
    public async openInModal(): Promise<void> {
        const chatKeyno = this.selectedChat?.ai_chat_keyno ?? '0';
        await this.modal.openStateDialog({ state: 'aichat', params: `?ai_chat_keyno=${encodeURIComponent(chatKeyno)}`, width: 'min(96vw, 100rem)', height: '90vh' });
    }

    /** Opens AI settings and refreshes chat configuration after the dialog closes. */
    public async openConfiguration(): Promise<void> {
        if (!this.panel.canEditAiConfiguration) return;

        await this.modal.openStateDialog({ state: 'aisettings' });
        await this.refreshConfiguration();
    }

    /** Refreshes configuration without replacing the conversation or showing loading states. */
    private async refreshConfiguration(): Promise<void> {
        const contextVersion = this.chatContextVersion;
        const selectionVersion = this.intelligenceLevelRequestVersion;

        try {
            const [settings, chats, models] = await Promise.all([this.aiChat.getAiChatPanel(), this.aiChat.getChats(), this.aiChat.getAiModels()]);
            this.panel.setSettings(settings);

            if (contextVersion !== this.chatContextVersion || selectionVersion !== this.intelligenceLevelRequestVersion || this.loadingIntelligenceLevels) return;

            const availableModels = this.selectedChat ? models.filter((model) => model.ai_provider_keyno === this.selectedChat?.ai_provider_keyno) : models;
            const selectedModel =
                availableModels.find((model) => model.ai_model_keyno === this.selectedModelKeyno) ??
                availableModels.find((model) => model.ai_model_keyno === this.getNewConversationModelKeyno(models)) ??
                availableModels[0];
            const levels = selectedModel ? await this.aiChat.getAiModelIntelligence({ ai_model_keyno: selectedModel.ai_model_keyno }) : [];

            if (contextVersion !== this.chatContextVersion || selectionVersion !== this.intelligenceLevelRequestVersion) return;

            if (this.selectedChat) this.selectedChat = chats.find((chat) => chat.ai_chat_keyno === this.selectedChat?.ai_chat_keyno) ?? this.selectedChat;
            this.selectedModelKeyno = selectedModel?.ai_model_keyno ?? '';
            this.intelligenceLevels = [...levels].sort((left, right) => Number(left.orderby) - Number(right.orderby));
            if (!this.intelligenceLevels.some((level) => level.intelligence_level_id === this.selectedIntelligenceLevel)) {
                this.selectedIntelligenceLevel = this.intelligenceLevels[0]?.intelligence_level_id ?? '';
            }
            this.newConversationModelKeyno = this.selectedModelKeyno;
            this.newConversationIntelligenceLevel = this.selectedIntelligenceLevel;
        } catch {
            // Keep the current conversation usable if the background refresh fails.
        } finally {
            this.cdr.markForCheck();
        }
    }

    /** Opens the resizable state-modal view for the selected conversation's tool calls. */
    public async openToolCalls(): Promise<void> {
        const aiChatKeyno = this.selectedChat?.ai_chat_keyno;

        if (!aiChatKeyno) return;

        await this.modal.openStateDialog({
            state: 'aichattoolcalls',
            params: `?ai_chat_keyno=${encodeURIComponent(aiChatKeyno)}`,
        });
    }

    private closeConversationDrawer() {
        if (this.chatsDrawer?.stateSubject.getValue()) void this.chatsDrawer.close();
    }

    ngOnInit(): void {
        if (this.navigator?.host === 'dialog') {
            void this.navigator.setHeading?.('ai_chat', true);
        }

        void this.loadInitialData();
    }
}
